import React, { useEffect, useRef, useState } from "react";
import * as firebaseui from "firebaseui";
import "firebaseui/dist/firebaseui.css";
import { GoogleAuthProvider, EmailAuthProvider } from "firebase/auth";
import { auth } from "../auth/firebaseConfig";
import "./Login.css";

function Login() {
  const uiRef = useRef(null);
  const uiInstance = useRef(null);
  const [mode, setMode] = useState('signin'); // 'signin' or 'signup'

  useEffect(() => {
    // Check if we have an instance, otherwise create one
    if (!uiInstance.current) {
      uiInstance.current = firebaseui.auth.AuthUI.getInstance() || new firebaseui.auth.AuthUI(auth);
    }

    // Reset FirebaseUI when mode changes
    if (uiRef.current && uiInstance.current) {
      uiInstance.current.reset();
    }

    const uiConfig = {
      signInOptions: [
        {
          provider: EmailAuthProvider.PROVIDER_ID,
          requireDisplayName: false, // Don't require display name
        },
        {
          provider: GoogleAuthProvider.PROVIDER_ID,
          customParameters: {
            prompt: 'select_account'
          }
        },
      ],
      signInFlow: 'popup',
      // Enable account chooser for email/password
      credentialHelper: firebaseui.auth.CredentialHelper.NONE,
      // Handle sign-up vs sign-in
      signInSuccessUrl: undefined, // We handle redirect manually
      callbacks: {
        signInSuccessWithAuthResult: async (authResult, redirectUrl) => {
          console.log('Sign in success:', { 
            isNewUser: authResult.additionalUserInfo?.isNewUser,
            mode,
            user: authResult.user?.email 
          });
          
          // Check if this is a new user (signup)
          const isNewUser = authResult.additionalUserInfo?.isNewUser;
          
          // Clear any existing signupRedirect flag first
          sessionStorage.removeItem('signupRedirect');
          
          // Only set signupRedirect if this is actually a signup
          if (mode === 'signup' || isNewUser) {
            sessionStorage.setItem('signupRedirect', 'true');
            console.log('Set signupRedirect flag for new user');
          } else {
            console.log('Cleared signupRedirect flag - existing user signing in');
          }
          
          // Close FirebaseUI popup/modal after a short delay
          setTimeout(() => {
            if (uiInstance.current) {
              uiInstance.current.reset();
            }
          }, 100);
          
          // The auth state change will be detected by AuthContext
          // and App.jsx will handle the redirect
          return false; // Prevent redirect, we handle it manually
        },
        signInFailure: (error) => {
          console.error('Sign in error:', error);
          // Don't show error here, FirebaseUI handles it
        },
        uiShown: () => {
          console.log('FirebaseUI shown');
        },
      },
      // Enable password reset (handled automatically by FirebaseUI)
      tosUrl: undefined,
      privacyPolicyUrl: undefined,
    };

    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      if (uiRef.current && uiInstance.current) {
        uiInstance.current.start(uiRef.current, uiConfig);
      }
    }, 100);

    return () => {
      clearTimeout(timer);
    };
  }, [mode]);

  // Update button text based on mode after FirebaseUI renders
  useEffect(() => {
    const updateButtonText = () => {
      // Update Google button text
      const googleButton = Array.from(document.querySelectorAll('.firebaseui-idp-text'))
        .find(btn => btn.textContent?.includes('Google') || btn.closest('.firebaseui-idp-google'));
      
      if (googleButton) {
        const newText = mode === 'signup' ? 'Sign up with Google' : 'Sign in with Google';
        if (googleButton.textContent !== newText) {
          googleButton.textContent = newText;
        }
      }

      // Update email button text
      const emailButton = Array.from(document.querySelectorAll('.firebaseui-idp-text'))
        .find(btn => {
          const text = btn.textContent?.toLowerCase();
          return (text?.includes('email') || text?.includes('sign in with email') || text?.includes('sign up with email')) 
            && !text?.includes('google');
        });
      
      if (emailButton) {
        const newText = mode === 'signup' ? 'Sign up with email' : 'Sign in with email';
        if (emailButton.textContent !== newText) {
          emailButton.textContent = newText;
        }
      }
    };

    const hideNameField = () => {
      // Hide name/displayName field in signup form
      const nameInputs = document.querySelectorAll('input[name="displayName"], input[type="text"]:not([name="email"]):not([type="password"])');
      nameInputs.forEach(input => {
        const textfield = input.closest('.mdl-textfield');
        if (textfield) {
          const label = textfield.querySelector('label');
          if (label && (label.textContent?.toLowerCase().includes('name') || label.textContent?.toLowerCase().includes('first') || label.textContent?.toLowerCase().includes('last'))) {
            textfield.style.display = 'none';
          }
        }
      });
    };

    // Try immediately
    updateButtonText();
    hideNameField();

    // Also try after delays (FirebaseUI renders async)
    const timers = [
      setTimeout(() => { updateButtonText(); hideNameField(); }, 200),
      setTimeout(() => { updateButtonText(); hideNameField(); }, 500),
      setTimeout(() => { updateButtonText(); hideNameField(); }, 1000),
    ];
    
    // Watch for DOM changes
    const observer = new MutationObserver(() => {
      updateButtonText();
      hideNameField();
    });
    if (uiRef.current) {
      observer.observe(uiRef.current, { childList: true, subtree: true, characterData: true });
    }

    return () => {
      timers.forEach(timer => clearTimeout(timer));
      observer.disconnect();
    };
  }, [mode]);

  return (
    <div className="login-container">
      <div className="login-card">
        <h1>Fund Transfer Service</h1>
        
        <div className="login-tabs">
          <button 
            className={`login-tab ${mode === 'signin' ? 'active' : ''}`}
            onClick={() => setMode('signin')}
          >
            Sign In
          </button>
          <button 
            className={`login-tab ${mode === 'signup' ? 'active' : ''}`}
            onClick={() => setMode('signup')}
          >
            Sign Up
          </button>
        </div>
        
        <p className="login-subtitle">
          {mode === 'signin' ? 'Sign in to your account' : 'Create a new account'}
        </p>
        
        <div ref={uiRef} id="firebaseui-auth-container" data-mode={mode}></div>
      </div>
    </div>
  );
}

export default Login;
